/** An address, in the parts a responder actually needs.
 *
 *  WHY NOT ONE BOX
 *
 *  A single line prints and does nothing else. The apartment is what somebody
 *  standing at a door needs and it is useless buried in a sentence; the postal
 *  code is the part a caller reads back accurately over a bad line and is the
 *  strongest thing to match on; and the day a geocoder is chosen, parts are
 *  what gets sent to it — one string is the input that makes geocoding guess.
 *
 *  The layout follows how somebody says an address out loud: number, then
 *  street, then which apartment, then the town. The number and apartment are
 *  narrow because they are short, and putting them on one line with the street
 *  keeps the shape of the thing recognisable rather than presenting eight
 *  identical boxes.
 */

export type Address = {
  house_number: string
  street: string
  unit: string
  building: string
  city: string
  region: string
  postal_code: string
  country: string
}

export const BLANK_ADDRESS: Address = {
  house_number: '', street: '', unit: '', building: '',
  city: '', region: '', postal_code: '', country: '',
}

/** Pull an address out of anything the server returned. */
export function addressFrom(source: Partial<Record<keyof Address, string | null>> | null | undefined): Address {
  return {
    house_number: source?.house_number ?? '',
    street: source?.street ?? '',
    unit: source?.unit ?? '',
    building: source?.building ?? '',
    city: source?.city ?? '',
    region: source?.region ?? '',
    postal_code: source?.postal_code ?? '',
    country: source?.country ?? '',
  }
}

/** Trimmed, with the empties dropped, ready to send. */
export function addressBody(a: Address): Record<string, string | null> {
  const out: Record<string, string | null> = {}
  for (const [k, v] of Object.entries(a)) out[k] = v.trim() || null
  return out
}

export function isEmpty(a: Address): boolean {
  return Object.values(a).every((v) => !v.trim())
}

export function AddressFields({
  value, onChange, defaults, showCountry = false,
}: {
  value: Address
  onChange: (next: Address) => void
  /** City, province and country filled in for the first time somebody uses
   *  this. Almost every caller an organization has is in one town, and making
   *  a dispatcher type it on every record is a way of ensuring it gets left
   *  blank. */
  defaults?: Partial<Address>
  /** Draw the country box. OFF for a dispatch address and ON for a postal one,
   *  which is the whole difference between the two uses.
   *
   *  `country` has been in `Address`, in `addressBody` and on `places` since
   *  structured addresses arrived, and this component has never drawn a box
   *  for it — so the column existed, was sent on every save, and could not be
   *  filled in from anywhere in the product. It went unnoticed because a
   *  dispatcher is never sent to another country and the field had nothing to
   *  do; a member's MAILING address is the first use that needs it, and a
   *  roster holding a Norwegian phone number will hold a Norwegian address.
   *
   *  Off by default so the caller-address form on the dispatch screen keeps
   *  the shape that was designed and approved — a box that is dead weight
   *  during a call is worse there than a missing one. */
  showCountry?: boolean
}) {
  const set = (k: keyof Address) => (e: React.ChangeEvent<HTMLInputElement>) =>
    onChange({ ...value, [k]: e.target.value })

  return (
    <div className="addressfields">
      <div className="addr-row street">
        <div className="field narrow">
          <label>Number</label>
          <input value={value.house_number} placeholder="42"
                 onChange={set('house_number')} />
        </div>
        <div className="field">
          <label>Street</label>
          <input value={value.street} placeholder="Oak Street"
                 onChange={set('street')} />
        </div>
        <div className="field narrow">
          <label>Apartment</label>
          <input value={value.unit} placeholder="3" onChange={set('unit')} />
        </div>
      </div>

      <div className="addr-row">
        <div className="field">
          <label>Building or floor</label>
          <input value={value.building} placeholder="Tower B"
                 onChange={set('building')} />
        </div>
        <div className="field">
          <label>City</label>
          <input
            value={value.city}
            placeholder={defaults?.city || 'Montreal'}
            onChange={set('city')}
          />
        </div>
        <div className="field narrow">
          <label>Province / state</label>
          <input
            value={value.region}
            placeholder={defaults?.region || 'QC'}
            onChange={set('region')}
          />
        </div>
        <div className="field narrow">
          <label>Postal / ZIP</label>
          <input
            value={value.postal_code}
            placeholder="H3A 1A1"
            onChange={set('postal_code')}
          />
        </div>
      </div>
          {showCountry && (
        <div className="addr-row">
          <div className="field">
            <label>Country</label>
            <input value={value.country} placeholder="Canada"
                   maxLength={80}
                   onChange={set('country')} />
          </div>
        </div>
      )}
</div>
  )
}
